import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Center, Spinner } from '@chakra-ui/react';
import {
  MMEmbeddableZone,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { palette } from '@m12s/component-library';

const HOLD_DELAY_MS = 500;
const FADE_DURATION_MS = 300;

// The timeout fallback prop accepts a node or a render function.
const resolveTimeoutFallback = (renderTimeoutFallback) => {
  if (typeof renderTimeoutFallback === 'function') {
    return renderTimeoutFallback() ?? null;
  }
  return renderTimeoutFallback ?? null;
};

/**
 * A wrapper around MMEmbeddableZone that shows a loading spinner
 * until the embedded app signals it's ready.
 *
 * After ready, holds the spinner for 500ms then fades it out.
 *
 * Optionally, when BOTH `timeoutMs` and `renderTimeoutFallback` are provided
 * and the embed has not posted 'ready' within `timeoutMs`, the spinner is
 * replaced by the fallback UI (the zone stays mounted, so a late 'ready'
 * still recovers). With either prop absent the behavior is exactly the
 * legacy one — spinner until ready, indefinitely.
 *
 * Accepts all props that MMEmbeddableZone accepts.
 */
const MMEmbeddableZoneWithLoader = ({
  id,
  timeoutMs,
  renderTimeoutFallback,
  ...props
}) => {
  const [isReady, setIsReady] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEmbeddedEffect(id, 'ready', () => {
    setIsReady(true);
    // A late ready (after the timeout fallback appeared) still recovers:
    // the embed is alive after all, so resume the normal hold/fade path.
    setIsTimedOut(false);
  });

  // Presence-only gate so re-renders that change the fallback's identity
  // (e.g. an inline render function) never restart the timeout timer.
  // Numeric presence (not truthiness) so timeoutMs=0 means an immediate
  // timeout — setTimeout semantics — rather than "disabled".
  const hasTimeoutFallback =
    Number.isFinite(timeoutMs) && Boolean(renderTimeoutFallback);

  useEffect(() => {
    if (isReady || !hasTimeoutFallback) {
      return undefined;
    }

    const timeoutTimer = setTimeout(() => {
      setIsTimedOut(true);
    }, timeoutMs);

    return () => {
      clearTimeout(timeoutTimer);
    };
  }, [isReady, hasTimeoutFallback, timeoutMs]);

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    // Hold for 500ms before starting fade
    const holdTimer = setTimeout(() => {
      setIsFading(true);
    }, HOLD_DELAY_MS);

    return () => {
      clearTimeout(holdTimer);
    };
  }, [isReady]);

  useEffect(() => {
    if (!isFading) {
      return undefined;
    }

    // After fade completes, fully hide the loader
    const fadeTimer = setTimeout(() => {
      setIsHidden(true);
    }, FADE_DURATION_MS);

    return () => {
      clearTimeout(fadeTimer);
    };
  }, [isFading]);

  // hasTimeoutFallback re-checked at render: if the fallback prop disappears
  // after the timeout fired, fall back to the spinner rather than a blank
  // overlay (either prop absent = exactly the legacy spinner behavior).
  const showTimeoutFallback = isTimedOut && !isReady && hasTimeoutFallback;

  return (
    <>
      {!isHidden && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          zIndex={1}
          opacity={isFading ? 0 : 1}
          transition={`opacity ${FADE_DURATION_MS}ms ease-out`}
        >
          {showTimeoutFallback ? (
            resolveTimeoutFallback(renderTimeoutFallback)
          ) : (
            <Spinner
              w="48px"
              h="48px"
              color={palette.Indigo600}
              thickness="4px"
            />
          )}
        </Center>
      )}
      <MMEmbeddableZone id={id} {...props} />
    </>
  );
};

MMEmbeddableZoneWithLoader.propTypes = {
  id: PropTypes.string.isRequired,
  /** How long to wait for the embed's 'ready' before showing the timeout
   * fallback. No timeout when absent (legacy behavior). */
  timeoutMs: PropTypes.number,
  /** Fallback UI (node, or a function returning one) shown in place of the
   * spinner once `timeoutMs` elapses without a 'ready'. */
  renderTimeoutFallback: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

MMEmbeddableZoneWithLoader.defaultProps = {
  timeoutMs: null,
  renderTimeoutFallback: null,
};

export default MMEmbeddableZoneWithLoader;
